import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { PENDING_IB_REFERRAL_KEY } from "features/referrals/constants";
import { useCheckReferralStatus } from "features/referrals/hooks/useCheckReferralStatus";

import {
  hasPendingIbReturn,
  pollReferralConfirmation,
  readIbReturnMarkers,
  resolveIbReferralId,
} from "../utils/ibReferralReturn";

// On return from the Healthlink Integrated Browser, confirm the referral and
// refresh the list — keeping the list in a loading state until it settles so
// the outdated list is never shown. The heavy lifting (marker reading, id
// resolution, status polling) lives in ../utils/ibReferralReturn.
export const useIbReferralReturn = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const patientId = useCurrentPatientId();
  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");
  const { mutateAsync: checkReferralStatus } = useCheckReferralStatus();

  // Seeded from the pending markers so the loader is up on first paint (no
  // stale-list flash) and cleared when processing settles.
  const [isProcessingReturn, setIsProcessingReturn] = useState(() =>
    hasPendingIbReturn(readIbReturnMarkers(searchParams))
  );

  useEffect(() => {
    const markers = readIbReturnMarkers(searchParams);

    if (!hasPendingIbReturn(markers)) return;

    const clearReturnParams = () => {
      sessionStorage.removeItem(PENDING_IB_REFERRAL_KEY);
      setSearchParams((previous) => {
        previous.delete("referralId");
        previous.delete("controlId");
        return previous;
      });
    };

    const runCheck = async () => {
      try {
        const referralId = await resolveIbReferralId({
          ...markers,
          patientId,
          tenantVal,
        });

        if (!referralId) {
          toast.error(
            "We couldn't match the Healthlink referral to update its status. Please check the referral status manually."
          );
        } else if (
          await pollReferralConfirmation(checkReferralStatus, referralId)
        ) {
          toast.success("Referral submitted to Healthlink successfully!");
        } else {
          toast.error(
            "Healthlink hasn't confirmed receipt of this referral yet. Please check the referral status manually."
          );
        }

        // Drop any cached list so the table refetches fresh once revealed — the
        // user never sees the pre-processing (outdated) referrals list.
        queryClient.removeQueries({ queryKey: ["referralsList"] });
      } finally {
        clearReturnParams();
        setIsProcessingReturn(false);
      }
    };

    runCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isProcessingReturn };
};
